'use client';

import './search-results.scss';

import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.js';

import { SearchBar } from '../../molecules/search-bar/search-bar';
import { getLegacyQuery, useUrlState } from '../../utils/search';
import { SearchResultsList } from './search-results-list';

const SearchResultsSchema = z.object({
  q: z.string().optional(),
  page: z.coerce.number().int().nonnegative().catch(0).optional(),
});

export type SearchResultsSchema = z.infer<typeof SearchResultsSchema>;

export const SearchResults = () => {
  const { t } = useTranslation();
  const { filters, updateSearchParams } = useUrlState(SearchResultsSchema);
  const searchParams = useSearchParams();
  const [itemOffset, setItemOffset] = useState<number>(0);
  const itemsPerPage = 10;

  useEffect(() => {
    updateSearchParams({
      q: searchParams.get('q') || '',
      page: searchParams.get('page') || undefined,
    });
  }, []);

  const handlePageChange = (page: number) => {
    updateSearchParams({ page: page.toString() });
    setItemOffset(itemsPerPage * page);
  };

  const handleQueryChange = (query: string) => {
    setItemOffset(0);
    updateSearchParams({ q: query, page: '0' });
  };

  // The legacy search uses a fragment like #stq=kenya.
  // That page will be redirected here, so if the query exists, let's use it.
  useEffect(() => {
    const legacyQuery = getLegacyQuery();
    if (legacyQuery) {
      handleQueryChange(legacyQuery);
    }
  }, []);

  return (
    <section className="SearchResults component">
      <div className="SearchResults__container container">
        <SearchBar
          title={t('skippy.siteSearch.searchResults')}
          titleSize="lg"
          placeholder={t('skippy.siteSearch.searchOurSite')}
          onQueryChange={(value) => handleQueryChange(value)}
          defaultQuery={filters.searchQuery}
          enableQuickSearch
        />
        <SearchResultsList
          itemOffset={itemOffset}
          queryText={filters.searchQuery}
          itemsPerPage={itemsPerPage}
          currentPage={filters.currentPage}
          onPageChange={(page) => handlePageChange(page)}
        />
      </div>
    </section>
  );
};

/**
 * Skeleton component for SearchResults.
 * Matches the dimensions and layout of the SearchResults component.
 */
export const SearchResultsSkeleton: React.FC<{
  itemCount?: number;
}> = ({ itemCount = 9 }) => {
  return (
    <section className="SearchResults component">
      <div className="SearchResults__container container">
        {/* SearchBar skeleton */}
        <div className="SearchBar">
          <div className="SearchBar__container">
            {/* Title skeleton */}
            <Skeleton height={32} width={120} className="mb-comfy-md" />
            {/* Search input skeleton */}
            <Skeleton height={48} width={384} borderRadius="0.5rem" />
          </div>
        </div>

        {/* TabGroup skeleton */}
        <div className="flex gap-snug-sm mb-comfy-lg">
          <Skeleton height={40} width={100} borderRadius="0.5rem" />
          <Skeleton height={40} width={120} borderRadius="0.5rem" />
          <Skeleton height={40} width={90} borderRadius="0.5rem" />
        </div>

        {/* Search results list skeleton */}
        <div className="flex flex-col gap-comfy-md">
          {/* Results count skeleton */}
          <Skeleton height={20} width={200} />

          {/* Results list skeleton */}
          <div className="flex flex-col gap-comfy-sm">
            {Array.from({ length: itemCount }, (_, index) => (
              <div
                key={index}
                className="flex gap-comfy-md p-comfy-md border border-neutral-mild rounded-lg"
              >
                {/* Badge skeleton */}
                <Skeleton height={24} width={80} borderRadius="9999px" />
                {/* Content skeleton */}
                <div className="flex-1 flex flex-col gap-snug-sm">
                  <Skeleton height={24} width="80%" />
                  <Skeleton height={16} width="100%" />
                  <Skeleton height={16} width="90%" />
                </div>
              </div>
            ))}
          </div>

          {/* Pagination skeleton */}
          <div className="flex justify-center mt-comfy-lg">
            <div className="flex gap-snug-sm">
              <Skeleton height={48} width={48} borderRadius="0.5rem" />
              <Skeleton height={48} width={48} borderRadius="0.5rem" />
              <Skeleton height={48} width={48} borderRadius="0.5rem" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
